// Mobile menu drawer (mockup 9c). Native <dialog> provides the focus trap, Escape and backdrop;
// native <details name> provides the accordions (the toggle listener covers older browsers).
if (!customElements.get('pv-mobile-menu')) {
  class PvMobileMenu extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.opener = null;
      this.categories = Array.from(this.querySelectorAll('.mobile-category'));

      this.onOpenerClick = (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.open(event.currentTarget);
      };
      this.bindOpeners();

      this.querySelector('[data-mobile-menu-close]')?.addEventListener('click', () => this.close());
      this.querySelector('[data-mobile-menu-search]')?.addEventListener('click', () => this.openSearch());

      // Clicks on the backdrop land on the dialog element itself, outside its box.
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
        if (!inside) this.close();
      });

      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('pv-menu-locked');
        this.openers.forEach((button) => button.setAttribute('aria-expanded', 'false'));
        if (this.opener?.isConnected && !this.skipFocusReturn) this.opener.focus();
        this.skipFocusReturn = false;
      });

      this.categories.forEach((category) =>
        category.addEventListener('toggle', () => {
          if (!category.open) return;
          this.categories.forEach((other) => {
            if (other !== category) other.open = false;
          });
        })
      );

      this.desktopQuery = window.matchMedia('(min-width: 1000px)');
      this.onBreakpointChange = (event) => {
        if (event.matches && this.dialog.open) this.close();
      };
      this.desktopQuery.addEventListener('change', this.onBreakpointChange);

      if (window.Shopify && window.Shopify.designMode) this.bindThemeEditor();
    }

    disconnectedCallback() {
      this.openers?.forEach((button) => button.removeEventListener('click', this.onOpenerClick, true));
      this.desktopQuery?.removeEventListener('change', this.onBreakpointChange);
      if (this.onSectionSelect) {
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }
      document.body.classList.remove('pv-menu-locked');
    }

    bindOpeners() {
      this.openers = Array.from(document.querySelectorAll('.header-burger-menu'));
      this.openers.forEach((button) => {
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', this.dialog.id);
        button.setAttribute('aria-expanded', 'false');
        // Capture phase so this runs before any legacy drawer listener on the same button.
        button.addEventListener('click', this.onOpenerClick, true);
      });
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      document.body.classList.add('pv-menu-locked');
      this.openers.forEach((button) => button.setAttribute('aria-expanded', String(button === opener)));
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    openSearch() {
      this.skipFocusReturn = true;
      this.close();
      document.querySelector('sticky-header [data-search-dialog-open]')?.click();
    }

    bindThemeEditor() {
      const sectionId = this.dataset.sectionId;
      this.onSectionSelect = (event) => {
        if (event.detail.sectionId === sectionId) this.open();
      };
      this.onSectionDeselect = (event) => {
        if (event.detail.sectionId === sectionId) this.close();
      };
      this.onBlockSelect = (event) => {
        if (event.detail.sectionId !== sectionId) return;
        this.open();
        const details = event.target.closest?.('details');
        if (details) details.open = true;
        event.target.scrollIntoView?.({ block: 'nearest' });
      };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  customElements.define('pv-mobile-menu', PvMobileMenu);
}
